"use client";

import {JSX} from "react";

import {useSnapshot} from "valtio";

import type {InquiryBookingInfo, InquiryFormInstance} from "@/features/rental/inquiry-form/inquiry-form.model";
import {BookingInfoBlock} from "@/features/rental/inquiry-form/parts/booking-info-block";

import {BookingSummaryCollapsible, SummaryRow, translateFeeLabel, translateTaxLabel} from "@/entities/booking";

import {useTranslation} from "@/shared/i18n";

interface InquiryPricingBlockProps {
    pricing: ReturnType<typeof useSnapshot<NonNullable<InquiryFormInstance["pricing"]>>>;
    bookingInfo?: InquiryBookingInfo | null;
    unitName?: string;
    showBookingInfo?: boolean;
}

export function InquiryPricingBlock({pricing, bookingInfo, unitName, showBookingInfo = true}: InquiryPricingBlockProps): JSX.Element {
    const {t, dict} = useTranslation("booking");

    const translatedFees = [...pricing.feesRows.map((fee) => ({...fee, label: translateFeeLabel(fee.label, t, dict)})), ...pricing.optionalFeesRows];
    const translatedTaxes = pricing.taxesRows.map((tax) => ({...tax, label: translateTaxLabel(tax.label, t, dict)}));

    return (
        <>
            {showBookingInfo && bookingInfo && <BookingInfoBlock info={bookingInfo} unitName={unitName} />}
            <SummaryRow label={t("rentNights", {nights: pricing.nightsCount})} amount={pricing.rentAmount} />
            {translatedFees.length > 0 && <BookingSummaryCollapsible title={t("fees")} rows={[...translatedFees]} />}
            {translatedTaxes.length > 0 && <BookingSummaryCollapsible title={t("taxes")} rows={[...translatedTaxes]} />}
            {pricing.couponDiscountAmount && <SummaryRow label={t("promoDiscount")} amount={pricing.couponDiscountAmount} discount />}
            <SummaryRow label={t("total")} amount={pricing.totalAmount} highlighted />
            {pricing.securityDepositsRows.length > 0 && (
                <BookingSummaryCollapsible title={t("securityDeposit")} rows={[...pricing.securityDepositsRows]} />
            )}
            {pricing.totalWithDeposits && <SummaryRow label={t("totalWithDeposits")} amount={pricing.totalWithDeposits} highlightedPlain />}
        </>
    );
}
